import React, { useState } from 'react';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { Typography, Box, Button, Card, CardContent, CardHeader, FormControl, GlobalStyles, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import OverallStandings from '../components/reporting/OverallStandings';
import ChartsDashboard from './results-v2/components/ChartsDashboard';
import PrintLayout from './results-v2/components/PrintLayout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import useResultsV2Logic from './results-v2/useResultsV2Logic';
import { PRINT_WINDOW_CSS } from './results-v2/components/PrintLayout';
import ReportSelector from './results-v2/components/ReportSelector';
import AffiliatedControl from './results-v2/components/AffiliatedControl';
import ByEventReport from './results-v2/components/ByEventReport';
import ByTeamReport from './results-v2/components/ByTeamReport';
import MissingReport from './results-v2/components/MissingReport';
import ByCategoryReport from './results-v2/components/ByCategoryReport';
import ByDivisionReport from './results-v2/components/ByDivisionReport';
import Scores1500Report from './results-v2/components/Scores1500Report';

const PRINT_SECTIONS = [
  { value: 'all', label: 'All Sections' },
  { value: 'events', label: 'Events Only' },
  { value: 'division', label: 'Division Only' },
  { value: 'category', label: 'Category Only' },
  { value: 'overall', label: 'Overall Only' },
];

const PRINT_STYLES = {
  '@media print': {
    '.no-print': { display: 'none !important' },
  },
};

const handlePrint = () => {
  const el = document.querySelector('.print-layout');
  if (!el) return;
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head><title>AMC Results</title>
<style>${PRINT_WINDOW_CSS}</style></head><body>${el.innerHTML}</body></html>`);
  win.document.close();
  win.print();
};

const ResultsV2 = () => {
  const [printSection, setPrintSection] = useState('all');

  const {
    eventsScores,
    eventsMap,
    teamsMapLocal,
    teams,
    teamModes,
    setTeamModes,
    setAutoAppliedModes,
    reportType,
    setReportType,
    selectedEvent,
    setSelectedEvent,
    selectedTeam,
    setSelectedTeam,
    selectedCategory,
    setSelectedCategory,
    selectedDivision,
    setSelectedDivision,
    affiliatedMode,
    setAffiliatedMode,
    categoriesMap,
    divisionsMap,
    adjustedTeamsMap,
    adjustedRows,
    byEventRows,
    eventHasAdjustments,
    missingRows,
    scores1500Rows,
    affiliatedTeamUids,
    manualOverrides,
    setManualOverrides,
    topN,
    setTopN,
    groupBy,
    setGroupBy,
    includeTies,
    setIncludeTies,
    groupedTopResults
  } = useResultsV2Logic();

  const missingColumns = [
    { field: 'eventNumber', headerName: 'Event Number', width: 125 },
    { field: 'eventName', headerName: 'Event Name', width: 250 },
    { field: 'teamNumber', headerName: 'Team Number', width: 125 },
    { field: 'teamName', headerName: 'Team Name', width: 225 },
    { field: 'rawScore', headerName: 'Score', width: 120 },
    { field: 'affiliated', headerName: 'Affiliated', width: 120, renderCell: (params) => params.value ? <CheckCircleIcon sx={{ color: 'green' }} /> : <CancelIcon sx={{ color: 'red' }} /> }
  ];

  return (
    <>
      <GlobalStyles styles={PRINT_STYLES} />
      <Grid2 className="no-print" sx={{ p: 3 }}>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader
            title="Print Results"
            subheader="Print award ceremony sheets in order: Events → Division → Category → Overall"
          />
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl variant="standard" sx={{ minWidth: 220 }}>
                <InputLabel>Section</InputLabel>
                <Select value={printSection} onChange={e => setPrintSection(e.target.value)}>
                  {PRINT_SECTIONS.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
                Print
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">Results V2 —</Typography>

            <ReportSelector
              reportType={reportType}
              setReportType={setReportType}
              eventsMap={eventsMap}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
              teams={teams}
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              categoriesMap={categoriesMap}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              divisionsMap={divisionsMap}
              selectedDivision={selectedDivision}
              setSelectedDivision={setSelectedDivision}
            />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AffiliatedControl affiliatedMode={affiliatedMode} setAffiliatedMode={setAffiliatedMode} />
        </Box>
      </Box>

      {reportType === 'dashboard' ? (
        <Box>
          <ChartsDashboard
            adjustedTeamsMap={adjustedTeamsMap}
            eventsScores={eventsScores}
            eventsMap={eventsMap}
            teams={teams}
            categoriesMap={categoriesMap}
            divisionsMap={divisionsMap}
            adjustedRows={adjustedRows}
          />
        </Box>
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          {reportType === 'overall' && (
            <OverallStandings
              adjustedRows={adjustedRows}
              teamModes={teamModes}
              setTeamModes={setTeamModes}
              setAutoAppliedModes={setAutoAppliedModes}
              affiliatedMode={affiliatedMode}
              affiliatedTeamUids={affiliatedTeamUids}
              manualOverrides={manualOverrides}
              setManualOverrides={setManualOverrides}
              groupedTopResults={groupedTopResults}
              eventsMap={eventsMap}
              eventsScores={eventsScores}
              categoriesMap={categoriesMap}
              divisionsMap={divisionsMap}
            />
          )}

          {reportType === 'byEvent' && (
            <ByEventReport
              selectedEvent={selectedEvent}
              eventsScores={eventsScores}
              categoriesMap={categoriesMap}
              divisionsMap={divisionsMap}
              byEventRows={byEventRows}
              eventHasAdjustments={eventHasAdjustments}
              teamModes={teamModes}
              setTeamModes={setTeamModes}
              setAutoAppliedModes={setAutoAppliedModes}
              teams={teams}
              setManualOverrides={setManualOverrides}
              topN={topN}
              includeTies={includeTies}
              groupedTopResults={groupedTopResults}
            />
          )}

          {reportType === 'byTeam' && (
            <ByTeamReport
              selectedTeam={selectedTeam}
              adjustedTeamsMap={adjustedTeamsMap}
              teams={teams}
              eventsScores={eventsScores}
              eventHasAdjustments={eventHasAdjustments}
              teamModes={teamModes}
              setTeamModes={setTeamModes}
              setAutoAppliedModes={setAutoAppliedModes}
              categoriesMap={categoriesMap}
              divisionsMap={divisionsMap}
              affiliatedMode={affiliatedMode}
              setManualOverrides={setManualOverrides}
            />
          )}

          {reportType === 'byCategory' && (
            <ByCategoryReport
              selectedCategory={selectedCategory}
              adjustedTeamsMap={adjustedTeamsMap}
              adjustedRows={adjustedRows}
              teams={teams}
              categoriesMap={categoriesMap}
              divisionsMap={divisionsMap}
              eventsScores={eventsScores}
              eventHasAdjustments={eventHasAdjustments}
              teamModes={teamModes}
              setTeamModes={setTeamModes}
              setAutoAppliedModes={setAutoAppliedModes}
              setManualOverrides={setManualOverrides}
              affiliatedMode={affiliatedMode}
            />
          )}

          {reportType === 'byDivision' && (
            <ByDivisionReport
              selectedDivision={selectedDivision}
              adjustedTeamsMap={adjustedTeamsMap}
              adjustedRows={adjustedRows}
              teams={teams}
              categoriesMap={categoriesMap}
              divisionsMap={divisionsMap}
              eventsScores={eventsScores}
              eventHasAdjustments={eventHasAdjustments}
              teamModes={teamModes}
              setTeamModes={setTeamModes}
              setAutoAppliedModes={setAutoAppliedModes}
              setManualOverrides={setManualOverrides}
              affiliatedMode={affiliatedMode}
            />
          )}

          {reportType === 'missing' && (
            <MissingReport missingRows={missingRows} missingColumns={missingColumns} eventHasAdjustments={eventHasAdjustments} eventsMap={eventsMap} teams={teams} teamsMapLocal={teamsMapLocal} eventsScores={eventsScores} />
          )}

          {reportType === 'scores1500' && (
            <Scores1500Report scores1500Rows={scores1500Rows} eventsMap={eventsMap} teams={teams} eventHasAdjustments={eventHasAdjustments} />
          )}

        </Box>
      )}

      </Grid2>

      <PrintLayout
        eventsScores={eventsScores}
        adjustedTeamsMap={adjustedTeamsMap}
        categoriesMap={categoriesMap}
        divisionsMap={divisionsMap}
        section={printSection}
      />
    </>
  );
};

export default ResultsV2;
